import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Grid, CircularProgress, Typography, Box, Snackbar, Alert, useMediaQuery
} from '@mui/material';
import { Delete, Visibility, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

const API_URL = window.location.hostname === 'localhost'
  ? "http://localhost:5400/admin"
  : "https://crmb.apitrading.lk/admin";

export default function AllQuotations({ userRole }) {
  const [quotations, setQuotations] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, quotation: null });
  const [selectedQuotationDetails, setSelectedQuotationDetails] = useState(null);

  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    fetchQuotations();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(fetchQuotations, 500);
    return () => clearTimeout(timeout);
  }, [search]);

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API_URL}/allquotations`);
      setQuotations(response.data.reverse());
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.quotation) return;

    try {
      await axios.delete(`${API_URL}/allquotations/${deleteDialog.quotation.id}`);
      setQuotations(quotations.filter(q => q.id !== deleteDialog.quotation.id));
      setSnackbar({ open: true, message: 'Quotation deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error deleting quotation:', error);
      setSnackbar({ open: true, message: 'Failed to delete quotation!', severity: 'error' });
    }

    setDeleteDialog({ open: false, quotation: null });
  };

  const handleView = (quotation) => {
    setSelectedQuotation(quotation);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQuotation(null);
  };

  const handleViewDetails = (quotation) => {
    setSelectedQuotationDetails(quotation);
    setOpen(true);
  };

  useEffect(() => {
    console.log("Quotations received:", quotations);
  }, [quotations]);

  const generatePDF = async (_id) => {
    if (!_id) {
      console.error("Quotation _id is undefined");
      setSnackbar({ open: true, message: 'Quotation ID is missing!', severity: 'error' });
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/quotation/${_id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Quotation_${_id}.pdf`;
      link.click();
      setSnackbar({ open: true, message: 'PDF downloaded successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({ open: true, message: 'Failed to generate PDF!', severity: 'error' });
    }
  };

  const handleBackup = async () => {
    try {
      const response = await fetch(`${API_URL}/backup`);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "backup.json";
      link.click();
      setSnackbar({ open: true, message: "Backup downloaded successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Error downloading backup!", severity: "error" });
    }
  };

  const handleRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_URL}/restore`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      setSnackbar({ open: true, message: data.message, severity: "success" });
      fetchQuotations(); // Refresh data after restore
    } catch (error) {
      setSnackbar({ open: true, message: "Error restoring data!", severity: "error" });
    }
  };

  const filteredQuotations = quotations.filter(q =>
    q.quotationNumber.toLowerCase().includes(search.toLowerCase()) ||
    q.brand.toLowerCase().includes(search.toLowerCase()) ||
    q.model.toLowerCase().includes(search.toLowerCase()) ||
    q.clientName.toLowerCase().includes(search.toLowerCase()) ||
    q.clientTelephone.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ padding: isMobile ? 2 : 3 }}>
      {/* Header Section */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: 'black',
            fontSize: isMobile ? '1.25rem' : '2rem'
          }}
        >
          All Quotations
        </Typography>
        {userRole === 'superadmin' && (
          <Box sx={{
            display: "flex",
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            width: isMobile ? '100%' : 'auto'
          }}>
            <Button
              variant="contained"
              onClick={handleBackup}
              fullWidth={isMobile}
              sx={{
                background: "linear-gradient(45deg, #F15A24, #e52e71)",
                color: "white",
                px: isMobile ? 2 : 3,
                py: 1,
                fontSize: isMobile ? '0.75rem' : '1rem',
                fontWeight: "bold",
                boxShadow: "0 4px 10px rgba(241, 90, 36, 0.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #e52e71, #F15A24)",
                  boxShadow: "0 6px 15px rgba(241, 90, 36, 0.4)",
                  transform: "translateY(-2px)",
                  transition: "all 0.3s ease"
                }
              }}
            >
              Backup Data
            </Button>
            <Button
              component="label"
              variant="contained"
              fullWidth={isMobile}
              sx={{
                background: "linear-gradient(45deg, #e52e71, #F15A24)",
                color: "white",
                px: isMobile ? 2 : 3,
                py: 1,
                fontSize: isMobile ? '0.75rem' : '1rem',
                fontWeight: "bold",
                boxShadow: "0 4px 10px rgba(241, 90, 36, 0.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #F15A24, #e52e71)",
                  boxShadow: "0 6px 15px rgba(241, 90, 36, 0.4)",
                  transform: "translateY(-2px)",
                  transition: "all 0.3s ease"
                }
              }}
            >
              Restore Data
              <input type="file" hidden onChange={handleRestore} />
            </Button>
          </Box>
        )}
      </Box>

      {/* Search Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <TextField
          label="Search Quotations"
          variant="outlined"
          fullWidth
          sx={{
            maxWidth: isMobile ? '100%' : '400px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#F15A24' },
              '&:hover fieldset': { borderColor: '#e52e71' },
              '&.Mui-focused fieldset': { borderColor: '#e52e71' },
              fontSize: isMobile ? '0.875rem' : '1rem',
            },
            '& .MuiInputLabel-root': {
              color: '#F15A24',
              '&.Mui-focused': { color: '#e52e71' },
              fontSize: isMobile ? '0.875rem' : '1rem',
            },
          }}
          onChange={(e) => setSearch(e.target.value)}
          size={isMobile ? 'small' : 'medium'}
        />
      </Box>

      {/* Table or Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3, overflowX: 'hidden' }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ background: '#333' }}>
                {['Quotation Number', 'Brand', 'Model', 'Client', 'Phone', 'Actions'].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '0.65rem' : '1rem',
                      padding: isMobile ? '6px' : '16px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQuotations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary" fontSize={isMobile ? '0.75rem' : '1rem'}>
                      No results found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuotations.map((q) => (
                  <TableRow key={q.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontSize: isMobile ? '0.65rem' : '0.9rem', padding: isMobile ? '6px' : '16px' }}>{q.quotationNumber}</TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.65rem' : '0.9rem', padding: isMobile ? '6px' : '16px' }}>{q.brand}</TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.65rem' : '0.9rem', padding: isMobile ? '6px' : '16px' }}>{q.model}</TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.65rem' : '0.9rem', padding: isMobile ? '6px' : '16px' }}>{q.clientName}</TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.65rem' : '0.9rem', padding: isMobile ? '6px' : '16px' }}>{q.clientTelephone}</TableCell>
                    <TableCell sx={{ padding: isMobile ? '6px' : '16px' }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 1,
                        alignItems: 'center'
                      }}>
                        <Button
                          onClick={() => handleView(q)}
                          sx={{
                            color: '#fff',
                            bgcolor: '#444',
                            borderRadius: '50px',
                            px: isMobile ? 1 : 3,
                            py: 0.5,
                            minWidth: isMobile ? '40px' : '100px',
                            '&:hover': { bgcolor: '#d14a1f' },
                            '&:focus': { bgcolor: '#d14a1f' },
                          }}
                        >
                          {isMobile ? (
                            <Visibility sx={{ color: '#fff', fontSize: '1rem' }} />
                          ) : (
                            <>
                              <Visibility sx={{ color: '#fff', fontSize: '1.25rem', mr: 1 }} />
                              View
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => generatePDF(q._id)}
                          sx={{
                            color: '#fff',
                            bgcolor: '#1976d2',
                            borderRadius: '50px',
                            px: isMobile ? 1 : 3,
                            py: 0.5,
                            minWidth: isMobile ? '40px' : '100px',
                            '&:hover': { bgcolor: '#1565c0' },
                            '&:focus': { bgcolor: '#1565c0' },
                          }}
                        >
                          {isMobile ? (
                            <PictureAsPdf sx={{ color: '#fff', fontSize: '1rem' }} />
                          ) : (
                            <>
                              <PictureAsPdf sx={{ color: '#fff', fontSize: '1.25rem', mr: 1 }} />
                              PDF
                            </>
                          )}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Quotation Details Dialog */}
      {selectedQuotation && (
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth={isMobile ? 'xs' : 'md'}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              background: 'rgba(206, 206, 206, 0.6)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(10px)',
              maxHeight: isMobile ? '80vh' : '90vh',
              width: isMobile ? '90%' : 'auto',
            }
          }}
        >
          <DialogTitle
            sx={{
              background: 'rgb(7, 0, 27)',
              color: 'white',
              fontSize: isMobile ? '1rem' : '1.5rem',
              textAlign: 'center',
              padding: isMobile ? '16px' : '24px',
              position: 'relative'
            }}
          >
            Quotation Details
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                backgroundColor: 'rgba(255, 0, 0, 0.68)',
                borderRadius: '50%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'rotate(90deg) scale(1.1)',
                  backgroundColor: 'rgba(255, 0, 0, 0.9)',
                  boxShadow: '0 0 8px rgba(255, 0, 0, 0.5)'
                }
              }}
            >
              <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ mt: 2, px: isMobile ? 2 : 4, overflowY: 'auto' }}>
            <Grid container spacing={isMobile ? 2 : 3}>
              {/* Client Information */}
              <Grid item xs={12}>
                <Box sx={{
                  p: isMobile ? 2 : 3,
                  border: '1px solid rgba(241,90,36,0.2)',
                  borderRadius: 3,
                  background: 'rgb(255, 255, 255)',
                }}>
                  <Typography variant="h6" sx={{
                    mb: 2,
                    fontSize: isMobile ? '1rem' : '1.3rem',
                    fontWeight: 700,
                    color: '#F15A24'
                  }}>
                    👤 Client Information
                  </Typography>
                  {[
                    { label: 'Name', value: selectedQuotation.clientName },
                    { label: 'Email', value: selectedQuotation.clientEmail },
                    { label: 'Telephone', value: selectedQuotation.clientTelephone },
                    { label: 'Address', value: selectedQuotation.clientAddress }
                  ].map((item) => (
                    <Typography key={item.label} variant="body1" sx={{
                      fontSize: isMobile ? '0.8rem' : '1.1rem',
                      color: '#333',
                      mb: 1
                    }}>
                      <strong>{item.label}:</strong> <span style={{ color: '#666' }}>{item.value}</span>
                    </Typography>
                  ))}
                </Box>
              </Grid>

              {/* Vehicle Information */}
              <Grid item xs={12}>
                <Box sx={{
                  p: isMobile ? 2 : 3,
                  border: '1px solid rgba(25,118,210,0.2)',
                  borderRadius: 3,
                  background: 'rgb(255, 255, 255)',
                }}>
                  <Typography variant="h6" sx={{
                    mb: 2,
                    fontSize: isMobile ? '1rem' : '1.3rem',
                    fontWeight: 700,
                    color: '#1976d2'
                  }}>
                    🚘 Vehicle Information
                  </Typography>
                  {[
                    { label: 'Brand', value: selectedQuotation.brand },
                    { label: 'Model', value: selectedQuotation.model },
                    { label: 'Year', value: selectedQuotation.year },
                    { label: 'Color', value: selectedQuotation.color }
                  ].map((item) => (
                    <Typography key={item.label} variant="body1" sx={{
                      fontSize: isMobile ? '0.8rem' : '1.1rem',
                      color: '#333',
                      mb: 1
                    }}>
                      <strong>{item.label}:</strong> <span style={{ color: '#666' }}>{item.value}</span>
                    </Typography>
                  ))}
                </Box>
              </Grid>

              {/* Finance Information */}
              <Grid item xs={12}>
                <Box sx={{
                  p: isMobile ? 2 : 3,
                  border: '1px solid rgba(76,175,80,0.2)',
                  borderRadius: 3,
                  background: 'rgb(255, 255, 255)',
                }}>
                  <Typography variant="h6" sx={{
                    mb: 2,
                    fontSize: isMobile ? '1rem' : '1.3rem',
                    fontWeight: 700,
                    color: '#4caf50'
                  }}>
                    💰 Finance Information
                  </Typography>
                  {[
                    { label: 'CIF Value', jpy: selectedQuotation.cifValueJPY, lkr: selectedQuotation.cifValueLKR },
                    { label: 'Custom Duties', lkr: selectedQuotation.customDutiesLKR },
                    { label: 'Clearance Charges', lkr: selectedQuotation.clearanceChargesLKR },
                    { label: 'Additional Charges', jpy: selectedQuotation.additionalChargesJPY, lkr: selectedQuotation.additionalChargesLKR }
                  ].map((item) => (
                    <Box key={item.label} sx={{ mb: 1 }}>
                      {item.jpy !== undefined && (
                        <Typography variant="body1" sx={{ fontSize: isMobile ? '0.8rem' : '1.1rem', color: '#666' }}>
                          <strong>{item.label} (JPY):</strong> ¥ {item.jpy}
                        </Typography>
                      )}
                      <Typography variant="body1" sx={{ fontSize: isMobile ? '0.8rem' : '1.1rem', color: '#666' }}>
                        <strong>{item.label} (LKR):</strong> Rs. {item.lkr}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Special Notes */}
              <Grid item xs={12}>
                <Box sx={{
                  p: isMobile ? 2 : 3,
                  border: '1px solid rgba(156,39,176,0.2)',
                  borderRadius: 3,
                  background: 'rgb(255, 255, 255)',
                }}>
                  <Typography variant="h6" sx={{
                    mb: 2,
                    fontSize: isMobile ? '1rem' : '1.3rem',
                    fontWeight: 700,
                    color: '#9c27b0'
                  }}>
                    📝 Special Notes
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.8rem' : '1.1rem',
                    color: '#666',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedQuotation.specialNotes}
                  </Typography>
                </Box>
              </Grid>

              {/* Options */}
              <Grid item xs={12}>
                <Box sx={{
                  p: isMobile ? 2 : 3,
                  border: '1px solid rgba(255,152,0,0.2)',
                  borderRadius: 3,
                  background: 'rgb(255, 255, 255)',
                }}>
                  <Typography variant="h6" sx={{
                    mb: 2,
                    fontSize: isMobile ? '1rem' : '1.3rem',
                    fontWeight: 700,
                    color: '#ff9800'
                  }}>
                    ⚙️ Options
                  </Typography>
                  {selectedQuotation.options.map((option, index) => (
                    <Box key={index} sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      justifyContent: 'space-between',
                      mb: 1
                    }}>
                      <Typography variant="subtitle1" sx={{ color: '#ff9800', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                        {option.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body1" sx={{ color: '#666', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                          JPY: ¥{option.priceJPY}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#666', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                          LKR: Rs.{option.priceLKR}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{
            display: 'flex',
            justifyContent: 'center',
            p: isMobile ? 0 : 1,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 0 : 0,
            background: 'linear-gradient(45deg, rgba(241,90,36,0.05) 0%, rgba(25,118,210,0.05) 100%)',
            backdropFilter: 'blur(4px)',
            borderRadius: '0 0 16px 16px',
            alignItems: 'center'
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: isMobile ? 0 : 3,
              width: '100%',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Box sx={{
                textAlign: 'center',
                p: isMobile ? 0.5 : 1,
                width: isMobile ? '100%' : '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.9)',
                
              }}>
                <Typography variant="subtitle2" sx={{
                  color: '#1976d2',
                  fontWeight: 600,
                  fontSize: isMobile ? '0.7rem' : '0.9rem',
                  mb: 0.25,
                  textShadow: '0 2px 4px rgba(25,118,210,0.2)'
                }}>
                  Total Amount
                </Typography>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: isMobile ? '1rem' : '1.2rem',
                  textShadow: '0 2px 4px rgba(25,118,210,0.2)'
                }}>
                  Rs. {selectedQuotation.total?.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{
                textAlign: 'center',
                p: isMobile ? 0.5 : 1,
                width: isMobile ? '100%' : '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.9)',
                
              }}>
                <Typography variant="subtitle2" sx={{
                  color: '#d32f2f',
                  fontWeight: 600,
                  fontSize: isMobile ? '0.7rem' : '0.9rem',
                  mb: 0.25,
                  textShadow: '0 2px 4px rgba(211,47,47,0.2)'
                }}>
                  Net Total
                </Typography>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  color: '#d32f2f',
                  fontSize: isMobile ? '1rem' : '1.2rem',
                  textShadow: '0 2px 4px rgba(211,47,47,0.2)'
                }}>
                  Rs. {selectedQuotation.netTotal?.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, quotation: null })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{
          backgroundColor: 'error.main',
          color: 'white',
          fontSize: isMobile ? '1rem' : '1.25rem'
        }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" fontSize={isMobile ? '0.8rem' : '1rem'}>
            Are you sure you want to delete <strong>{deleteDialog.quotation?.quotation_number}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, quotation: null })}
            color="primary"
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            No
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Quotation Details Dialog */}
      <Dialog
        open={!!selectedQuotationDetails}
        onClose={() => setSelectedQuotationDetails(null)}
        fullWidth
        maxWidth={isMobile ? 'xs' : 'sm'}
      >
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          fontSize: isMobile ? '1rem' : '1.25rem'
        }}>
          Quotation Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedQuotationDetails && Object.entries(selectedQuotationDetails).map(([key, value]) => (
            <Typography
              key={key}
              variant="body1"
              sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '1rem' }}
            >
              <strong>{key.replace(/_/g, ' ')}:</strong> {Array.isArray(value) ? value.map(option => `${option.name} (JPY: ${option.priceJPY}, LKR: ${option.priceLKR})`).join(', ') : value}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedQuotationDetails(null)}
            color="primary"
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
